import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faXmark,
  faPercent,
  faHandshake,
  faSave,
  faTags,
  faCircleInfo,
  faGaugeHigh,
  faCalendarDays,
  faUserPlus,
} from '@fortawesome/pro-solid-svg-icons';
import { PartnerService } from '../../../../../core/services/partner.service';
import {
  Partner,
  PARTNER_TYPE_OPTIONS,
  SETTLEMENT_PERIODICITY_OPTIONS,
  DISTRIBUTION_MODALITY_OPTIONS,
} from '../../../../../core/models/partner.model';
import { USER_TYPE_OPTIONS } from '../../../../../core/models/user.model';
import { PopupInfoService } from '../../../../../shared/services/popup-info.service';
import { CustomSelectComponent } from '../../../../../shared/components/custom-select/custom-select.component';

@Component({
  selector: 'app-edit-partner-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    CustomSelectComponent,
  ],
  templateUrl: './edit-partner-modal.html',
  styleUrls: ['./edit-partner-modal.scss'],
})
export class EditPartnerModal {
  private readonly fb = inject(FormBuilder);
  private readonly partnerService = inject(PartnerService);
  private readonly popupInfoService = inject(PopupInfoService);

  @Input() partner!: Partner;
  @Output() close = new EventEmitter<void>();
  @Output() success = new EventEmitter<Partner>();

  faXmark = faXmark;
  faPercent = faPercent;
  faHandshake = faHandshake;
  faSave = faSave;
  faTags = faTags;
  faCircleInfo = faCircleInfo;
  faGaugeHigh = faGaugeHigh;
  faCalendarDays = faCalendarDays;
  faUserPlus = faUserPlus;

  showRelationshipInfoModal = false;

  typeOptions = PARTNER_TYPE_OPTIONS;
  relationshipTypeOptions = USER_TYPE_OPTIONS.filter((option) => option.value !== 'Administrador');
  settlementPeriodicityOptions = SETTLEMENT_PERIODICITY_OPTIONS;
  distributionModalityOptions = DISTRIBUTION_MODALITY_OPTIONS;
  readonly commentsMaxLength = 500;

  private readonly distributionModalityDefaultCommissions: Record<
    string,
    { fixed: number; recurring: number }
  > = {
    'Reventa': { fixed: 10, recurring: 5 },
    'Distribución Comercial': { fixed: 10, recurring: 5 },
    'Modelo Mixto': { fixed: 15, recurring: 10 },
    'Distribución Integral': { fixed: 30, recurring: 20 },
  };

  form: FormGroup;
  isLoading = false;

  constructor() {
    this.form = this.fb.group({
      type: ['', Validators.required],
      comments: ['', Validators.maxLength(500)],
      relationship_type: [''],
      distribution_modality: [''],
      fixed_commission: [null, [Validators.min(0), Validators.max(100)]],
      recurring_commission: [
        5,
        [Validators.required, Validators.min(0), Validators.max(100)],
      ],
      operation_limit: [null, Validators.min(0)],
      settlement_periodicity: [''],
      min_new_clients_per_year: [null, Validators.min(0)],
    });

    this.form.get('relationship_type')?.valueChanges.subscribe((value) => {
      const distributionModalityControl = this.form.get('distribution_modality');
      if (value === 'Distribuidor') {
        distributionModalityControl?.setValidators(Validators.required);
      } else {
        distributionModalityControl?.clearValidators();
        distributionModalityControl?.setValue('');
      }
      distributionModalityControl?.updateValueAndValidity();

      const fixedCommissionControl = this.form.get('fixed_commission');
      const recurringCommissionControl = this.form.get('recurring_commission');
      const operationLimitControl = this.form.get('operation_limit');
      const settlementPeriodicityControl = this.form.get('settlement_periodicity');
      const minNewClientsControl = this.form.get('min_new_clients_per_year');

      if (value === 'Colaborador') {
        fixedCommissionControl?.clearValidators();
        fixedCommissionControl?.setValue(null);
        recurringCommissionControl?.clearValidators();
        recurringCommissionControl?.setValue(0);
        operationLimitControl?.clearValidators();
        operationLimitControl?.setValue(null);
        settlementPeriodicityControl?.setValue('');
        minNewClientsControl?.clearValidators();
        minNewClientsControl?.setValue(null);
      } else {
        fixedCommissionControl?.setValidators([Validators.min(0), Validators.max(100)]);
        recurringCommissionControl?.setValidators([
          Validators.required,
          Validators.min(0),
          Validators.max(100),
        ]);
        operationLimitControl?.setValidators(Validators.min(0));
        minNewClientsControl?.setValidators(Validators.min(0));
      }
      fixedCommissionControl?.updateValueAndValidity();
      recurringCommissionControl?.updateValueAndValidity();
      operationLimitControl?.updateValueAndValidity();
      minNewClientsControl?.updateValueAndValidity();
    });

    this.form.get('distribution_modality')?.valueChanges.subscribe((value) => {
      if (this.form.get('relationship_type')?.value !== 'Distribuidor') return;
      const defaults = this.distributionModalityDefaultCommissions[value];
      if (defaults) {
        this.form.patchValue({
          fixed_commission: defaults.fixed,
          recurring_commission: defaults.recurring,
        });
      }
    });
  }

  get isDistribuidor(): boolean {
    return this.form.get('relationship_type')?.value === 'Distribuidor';
  }

  get isColaborador(): boolean {
    return this.form.get('relationship_type')?.value === 'Colaborador';
  }

  ngOnInit(): void {
    if (this.partner) {
      const currentRelationshipType = this.relationshipTypeOptions.some(
        (option) => option.value === this.partner.relationship_type,
      )
        ? this.partner.relationship_type
        : '';

      this.form.patchValue({
        type: this.partner.type || '',
        comments: this.partner.comments || '',
        relationship_type: currentRelationshipType,
        distribution_modality: this.partner.distribution_modality || '',
        fixed_commission: this.partner.fixed_commission ?? null,
        recurring_commission: this.partner.recurring_commission ?? 5,
        operation_limit: this.partner.operation_limit ?? null,
        settlement_periodicity: this.partner.settlement_periodicity || '',
        min_new_clients_per_year: this.partner.min_new_clients_per_year ?? null,
      });
    }
  }

  onClose(): void {
    this.close.emit();
  }

  openRelationshipInfoModal(): void {
    this.showRelationshipInfoModal = true;
  }

  closeRelationshipInfoModal(): void {
    this.showRelationshipInfoModal = false;
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const { relationship_type, settlement_periodicity, distribution_modality, ...data } =
      this.form.getRawValue();
    if (relationship_type) {
      data.relationship_type = relationship_type;
    }
    if (settlement_periodicity) {
      data.settlement_periodicity = settlement_periodicity;
    }
    if (relationship_type === 'Distribuidor' && distribution_modality) {
      data.distribution_modality = distribution_modality;
    } else if (relationship_type && relationship_type !== 'Distribuidor') {
      data.distribution_modality = null;
    }

    this.partnerService.updatePartner(this.partner.id, data).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.popupInfoService.showSuccess('Partner actualizado correctamente');
        this.success.emit(response.data);
      },
      error: (err) => {
        this.isLoading = false;
        this.popupInfoService.showError('Error al actualizar el partner');
        console.error('Error updating partner:', err);
      },
    });
  }
}
